import React, { useState, useMemo } from "react";
import { FiChevronDown, FiChevronRight, FiCheck } from "react-icons/fi";
import useRecurringGroups from "./useRecurringGroups";
import useReviewedItems from "./useReviewedItems";

// Format currency
const formatCurrency = (value) => {
	return Math.abs(value).toLocaleString("en-US", {
		style: "currency",
		currency: "USD",
		minimumFractionDigits: 2,
	});
};

// Format date
const formatDate = (dateStr) => {
	const date = new Date(dateStr);
	return date.toLocaleDateString("en-US", {
		year: "numeric",
		month: "short",
		day: "numeric",
	});
};

// Get default date range (last 12 months)
const getDefaultDateRange = () => {
	const end = new Date();
	const start = new Date();
	start.setFullYear(start.getFullYear() - 1);

	return {
		start: start.toISOString().split("T")[0],
		end: end.toISOString().split("T")[0],
	};
};

// Transaction row component
const TransactionRow = ({ transaction, isReviewed, onToggleReviewed }) => {
	const amount = Number(transaction.transaction) || 0;

	return (
		<div className={`reoccurring__transaction ${isReviewed ? "reoccurring__transaction--reviewed" : ""}`}>
			<label className="reoccurring__checkbox">
				<input
					type="checkbox"
					checked={isReviewed}
					onChange={() => onToggleReviewed(transaction.accId)}
				/>
				<span className="reoccurring__checkbox-mark">
					{isReviewed && <FiCheck />}
				</span>
			</label>
			<span className="reoccurring__transaction-date">{formatDate(transaction.date)}</span>
			<span className="reoccurring__transaction-amount">{formatCurrency(amount)}</span>
			<span className="reoccurring__transaction-desc">{transaction.description}</span>
			<span className="reoccurring__transaction-account">{transaction.account}</span>
		</div>
	);
};

// Merchant group component (within a category)
const MerchantGroup = ({
	group,
	isGroupReviewed,
	isTransactionReviewed,
	onToggleGroupReviewed,
	onToggleTransactionReviewed,
}) => {
	const [isExpanded, setIsExpanded] = useState(false);
	const hasMultipleDescriptions = group.matchedDescriptions.length > 1;

	return (
		<div className={`reoccurring__merchant ${isGroupReviewed ? "reoccurring__merchant--reviewed" : ""}`}>
			<div className="reoccurring__merchant-header" onClick={() => setIsExpanded(!isExpanded)}>
				<label className="reoccurring__checkbox" onClick={(e) => e.stopPropagation()}>
					<input
						type="checkbox"
						checked={isGroupReviewed}
						onChange={() => onToggleGroupReviewed(group.id)}
					/>
					<span className="reoccurring__checkbox-mark">
						{isGroupReviewed && <FiCheck />}
					</span>
				</label>

				<span className="reoccurring__merchant-name">{group.name}</span>

				<span className="reoccurring__merchant-stats">
					{group.count}x &middot; {formatCurrency(group.avgAmount)} avg &middot; {formatCurrency(group.totalAmount)}
				</span>

				<span className={`reoccurring__merchant-arrow ${isExpanded ? "expanded" : ""}`}>
					<FiChevronRight />
				</span>
			</div>

			{hasMultipleDescriptions && (
				<div className="reoccurring__merchant-matched">
					Matched: {group.matchedDescriptions.slice(0, 3).join(", ")}
					{group.matchedDescriptions.length > 3 && ` +${group.matchedDescriptions.length - 3} more`}
				</div>
			)}

			{isExpanded && (
				<div className="reoccurring__transactions">
					{group.transactions.map((transaction) => (
						<TransactionRow
							key={transaction.accId}
							transaction={transaction}
							isReviewed={isTransactionReviewed(transaction.accId)}
							onToggleReviewed={onToggleTransactionReviewed}
						/>
					))}
				</div>
			)}
		</div>
	);
};

// Category section component
const CategorySection = ({
	category,
	isGroupReviewed,
	isTransactionReviewed,
	onToggleGroupReviewed,
	onToggleTransactionReviewed,
	minOccurrences,
	hideReviewed,
}) => {
	const [isExpanded, setIsExpanded] = useState(true);

	// Filter groups within category
	const filteredGroups = useMemo(() => {
		let result = category.groups;
		result = result.filter((g) => g.count >= minOccurrences);
		if (hideReviewed) {
			result = result.filter((g) => !isGroupReviewed(g.id));
		}
		return result;
	}, [category.groups, minOccurrences, hideReviewed, isGroupReviewed]);

	if (filteredGroups.length === 0) return null;

	const totalAmount = filteredGroups.reduce((sum, g) => sum + g.totalAmount, 0);

	return (
		<div className="reoccurring__category">
			<div className="reoccurring__category-header" onClick={() => setIsExpanded(!isExpanded)}>
				<span className={`reoccurring__category-arrow ${isExpanded ? "expanded" : ""}`}>
					<FiChevronDown />
				</span>
				<span className="reoccurring__category-name">{category.category}</span>
				<span className="reoccurring__category-stats">
					{filteredGroups.length} merchants &middot; {formatCurrency(totalAmount)}
				</span>
			</div>

			{isExpanded && (
				<div className="reoccurring__category-content">
					{filteredGroups.map((group) => (
						<MerchantGroup
							key={group.id}
							group={group}
							isGroupReviewed={isGroupReviewed(group.id)}
							isTransactionReviewed={isTransactionReviewed}
							onToggleGroupReviewed={onToggleGroupReviewed}
							onToggleTransactionReviewed={onToggleTransactionReviewed}
						/>
					))}
				</div>
			)}
		</div>
	);
};

// Main component
const Reoccurring = () => {
	const defaultRange = getDefaultDateRange();

	// Filter state
	const [startDate, setStartDate] = useState(defaultRange.start);
	const [endDate, setEndDate] = useState(defaultRange.end);
	const [selectedAccounts, setSelectedAccounts] = useState([]);
	const [hideReviewed, setHideReviewed] = useState(false);
	const [minOccurrences, setMinOccurrences] = useState(2);

	// Data hooks - now returns categories
	const { groups: categories, loading, error, loadData, hasLoaded, availableAccounts } = useRecurringGroups({
		startDate,
		endDate,
		selectedAccounts,
	});

	const {
		isGroupReviewed,
		isTransactionReviewed,
		toggleGroupReviewed,
		toggleTransactionReviewed,
		reviewedGroupCount,
	} = useReviewedItems();

	// Count totals
	const totals = useMemo(() => {
		let categoryCount = 0;
		let merchantCount = 0;
		let totalAmount = 0;

		categories.forEach((cat) => {
			const filtered = cat.groups.filter((g) => {
				if (g.count < minOccurrences) return false;
				if (hideReviewed && isGroupReviewed(g.id)) return false;
				return true;
			});
			if (filtered.length > 0) {
				categoryCount++;
				merchantCount += filtered.length;
				totalAmount += filtered.reduce((sum, g) => sum + g.totalAmount, 0);
			}
		});

		return { categoryCount, merchantCount, totalAmount };
	}, [categories, minOccurrences, hideReviewed, isGroupReviewed]);

	// Handle account selection
	const handleAccountToggle = (account) => {
		setSelectedAccounts((prev) =>
			prev.includes(account) ? prev.filter((a) => a !== account) : [...prev, account]
		);
	};

	const handleSelectAll = () => {
		setSelectedAccounts([...availableAccounts]);
	};

	const handleClearAll = () => {
		setSelectedAccounts([]);
	};

	return (
		<div className="lavella page dark-mode-disabled reoccurring-page">
			<section>
				<div className="cont--lg">
					<div className="reoccurring">
						<header className="reoccurring__header">
							<h1>Recurring Transactions</h1>
						</header>

						{/* Filters Panel */}
						<div className="reoccurring__panel">
							<div className="reoccurring__row">
								<div className="reoccurring__field">
									<label>Start</label>
									<input
										type="date"
										value={startDate}
										onChange={(e) => setStartDate(e.target.value)}
									/>
								</div>
								<div className="reoccurring__field">
									<label>End</label>
									<input
										type="date"
										value={endDate}
										onChange={(e) => setEndDate(e.target.value)}
									/>
								</div>
							</div>

							{/* Accounts Section */}
							<div className="reoccurring__section">
								<div className="reoccurring__section-header">
									<span>Accounts {availableAccounts.length > 0 && `(${availableAccounts.length})`}</span>
									<div className="reoccurring__section-actions">
										<button onClick={handleSelectAll} disabled={availableAccounts.length === 0}>All</button>
										<button onClick={handleClearAll}>None</button>
									</div>
								</div>
								{availableAccounts.length === 0 ? (
									<p className="reoccurring__hint">Click "Load Data" first to see available accounts</p>
								) : (
									<div className="reoccurring__accounts-list">
										{availableAccounts.map((account) => (
											<label key={account} className="reoccurring__account-item">
												<input
													type="checkbox"
													checked={selectedAccounts.includes(account)}
													onChange={() => handleAccountToggle(account)}
												/>
												<span>{account}</span>
											</label>
										))}
									</div>
								)}
								{selectedAccounts.length === 0 && availableAccounts.length > 0 && (
									<p className="reoccurring__hint">No accounts selected = all accounts</p>
								)}
							</div>

							<button
								className="reoccurring__load-btn"
								onClick={loadData}
								disabled={loading}
							>
								{loading ? "Loading..." : hasLoaded ? "Reload Data" : "Load Data"}
							</button>
						</div>

						{/* Results Controls */}
						{hasLoaded && !loading && categories.length > 0 && (
							<div className="reoccurring__controls">
								<label className="reoccurring__control">
									<input
										type="checkbox"
										checked={hideReviewed}
										onChange={(e) => setHideReviewed(e.target.checked)}
									/>
									<span>Hide reviewed ({reviewedGroupCount})</span>
								</label>
								<div className="reoccurring__control">
									<label>Min:</label>
									<input
										type="number"
										min="1"
										value={minOccurrences}
										onChange={(e) => setMinOccurrences(Number(e.target.value) || 1)}
									/>
								</div>
								<span className="reoccurring__count">
									{totals.categoryCount} categories &middot; {totals.merchantCount} merchants &middot; {formatCurrency(totals.totalAmount)}
								</span>
							</div>
						)}

						{/* Results */}
						<div className="reoccurring__results">
							{!hasLoaded && !loading && (
								<div className="reoccurring__empty">
									Select date range, then click <strong>Load Data</strong>.
								</div>
							)}

							{loading && <div className="reoccurring__loading">Loading...</div>}

							{error && <div className="reoccurring__error">{error}</div>}

							{hasLoaded && !loading && !error && totals.merchantCount === 0 && (
								<div className="reoccurring__empty">
									No recurring transactions found.
								</div>
							)}

							{hasLoaded && !loading && !error && totals.merchantCount > 0 && (
								<div className="reoccurring__categories">
									{categories.map((category) => (
										<CategorySection
											key={category.category}
											category={category}
											isGroupReviewed={isGroupReviewed}
											isTransactionReviewed={isTransactionReviewed}
											onToggleGroupReviewed={toggleGroupReviewed}
											onToggleTransactionReviewed={toggleTransactionReviewed}
											minOccurrences={minOccurrences}
											hideReviewed={hideReviewed}
										/>
									))}
								</div>
							)}
						</div>
					</div>
				</div>
			</section>
		</div>
	);
};

export default Reoccurring;
